import { FC, useEffect, useState } from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import CustomInput from 'components/custom-input.tsx/CustomInput';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { CommissionType } from '../../commission-settings/constants';
import CommissionRate from '../../components/CommissionRate';
import { useCreateExternalCompanyMutation } from '../api';
import { REGEX } from 'constants/common';
import { isEmailEndingValid } from 'utils/commonUtils';
import { getPhoneNumberDigits, isValidPhoneNumber } from 'utils/phoneNumberUtils';
import { CommissionBaseItem } from 'containers/app/app.model';

interface CreateExternalCompanyModalProps {
  open: boolean;
  onClose: () => void;
  onCreateExternalCompany: (company: { id: string; name: string }) => void;
  initialData?: {
    companyName: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}

const CreateExternalCompanyModal: FC<CreateExternalCompanyModalProps> = ({
  open,
  onClose,
  onCreateExternalCompany,
  initialData
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [createExternalCompany, { isLoading }] = useCreateExternalCompanyMutation();

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [commissionRateData, setCommissionRateData] = useState({
    value: '',
    unit: CommissionType.ABSOLUTE,
    baseType: null
  });

  // Validation states
  const [errors, setErrors] = useState({
    companyName: '',
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    commissionRate: false,
    commissionBase: false
  });

  useEffect(() => {
    if (initialData) {
      setCompanyName(initialData.companyName);
    }
  }, [initialData]);

  const resetForm = () => {
    setCompanyName('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setCommissionRateData({
      value: '',
      unit: CommissionType.ABSOLUTE,
      baseType: null
    });
    setErrors({
      companyName: '',
      firstName: false,
      lastName: false,
      email: false,
      phone: false,
      commissionRate: false,
      commissionBase: false
    });
  };

  const validateForm = () => {
    const newErrors = {
      companyName: companyName.trim() ? '' : 'Company name is required',
      firstName: false,
      lastName: false,
      email: email.trim() ? !REGEX.email.test(email) || !isEmailEndingValid(email) : false,
      phone: phone.trim() ? !isValidPhoneNumber(getPhoneNumberDigits(phone)) : false,
      commissionRate: false,
      commissionBase:
        commissionRateData.value && commissionRateData.unit === CommissionType.PERCENTAGE
          ? !commissionRateData.baseType
          : false
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      teamId: currentTeam?.id?.toString() || '',
      companyName,
      companyPocFirstname: firstName,
      companyPocLastname: lastName,
      companyPocEmail: email,
      companyPocPhone: phone ? getPhoneNumberDigits(phone) : '',
      commissionType: commissionRateData.value ? (commissionRateData.unit as CommissionType) : null,
      commissionValue: parseFloat(commissionRateData.value),
      commissionApplyOn: commissionRateData.baseType?.key || ''
    };

    try {
      const response = await createExternalCompany(payload).unwrap();
      if (response) {
        onCreateExternalCompany({ id: response.id, name: response.companyName });
      }
      handleClose();
    } catch (error) {
      // Error handling
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCommissionRateChange = (data: { value: string; unit: string; baseType?: CommissionBaseItem }) => {
    setCommissionRateData({
      value: data.value,
      unit: data.unit as CommissionType,
      baseType: data.baseType || null
    });
    if (data.unit === CommissionType.ABSOLUTE) {
      setErrors(prev => ({ ...prev, commissionBase: false }));
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value;
    if (REGEX.phoneNumberInput.test(value)) {
      setPhone(value);
      setErrors(prev => ({ ...prev, phone: false }));
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setErrors(prev => ({ ...prev, email: false }));
  };

  const handleNameChange = (value: string) => {
    setCompanyName(value);
    setErrors(prev => ({ ...prev, companyName: '' }));
  };

  return (
    <CustomModal
      enableOutsideClick={false}
      open={open}
      closeModal={handleClose}
      className=""
      backgroundColor="bg-white"
      width="md:w-[610px] max-w-[90vw]">
      <div className="flex flex-col p-6">
        <h2 className="mb-6 text-xl font-bold text-headingGray">Add company profile</h2>

        <div className="space-y-6">
          {/* Company Name */}
          <div>
            <label className="mb-2 block text-17px font-semibold text-headingGray">
              Company name<span className="text-red-500">*</span>
            </label>
            <CustomInput
              id="company-name"
              value={companyName}
              onChange={handleNameChange}
              placeholder="Enter company name"
              error={errors.companyName}
              className="text-17px font-normal text-primaryText outline-none"
              maxLength={30}
            />
          </div>

          {/* Primary Contact */}
          <div>
            <label className="mb-2 block text-17px font-semibold text-headingGray">Primary contact</label>
            <div className="grid grid-cols-2 gap-4">
              <CustomInput
                id="first-name"
                value={firstName}
                onChange={value => setFirstName(value)}
                placeholder="First name"
                error={errors.firstName ? 'First name is required' : ''}
                className="text-17px font-normal text-primaryText outline-none"
              />
              <CustomInput
                id="last-name"
                value={lastName}
                onChange={value => setLastName(value)}
                placeholder="Last name"
                error={errors.lastName ? 'Last name is required' : ''}
                className="text-17px font-normal text-primaryText outline-none"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-17px font-semibold text-headingGray">Phone number</label>
              <div
                className="flex h-[50px] w-full appearance-none flex-row items-center rounded
              bg-secondaryBtn px-4 py-3 leading-tight">
                <div className="mr-1 text-17px text-accentText">+1</div>
                <input
                  type="text"
                  id="company-phone"
                  placeholder="(000) 00-0000"
                  className="appearance-none bg-secondaryBtn text-17px font-normal
                text-primaryText outline-none focus:outline-none"
                  value={phone}
                  onChange={handlePhoneChange}
                />
              </div>
              {errors.phone && (
                <div className="mt-1 text-sbase font-semibold text-error">Please enter a valid phone number</div>
              )}
            </div>
            <div>
              <label className="mb-2 block text-17px font-semibold text-headingGray">Email Id</label>
              <CustomInput
                id="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter email address"
                error={errors.email ? 'Please enter a valid email address' : ''}
                className="text-17px font-normal text-primaryText outline-none"
              />
            </div>
          </div>

          {/* Contract Section */}
          <div>
            <div className="mb-2 text-17px font-semibold text-headingGray">Default referral rate</div>
            <CommissionRate
              onChange={handleCommissionRateChange}
              initialData={commissionRateData}
              isExternalCompany={true}
              isListDropdownPortal={true}
            />
            {errors.commissionBase && (
              <div className="mt-1 text-sbase font-semibold text-error">Please select a valid base</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-end gap-4">
          <CustomButton
            id="cancel-external-company-button"
            type={ButtonType.SECONDARY}
            onClick={handleClose}
            className="h-10"
            childrenContainerStyles="text-17px font-semibold">
            Cancel
          </CustomButton>
          <CustomButton
            id="create-external-company-button"
            type={ButtonType.PRIMARY}
            onClick={handleSubmit}
            disabled={isLoading}
            className="h-10"
            childrenContainerStyles="text-17px font-semibold">
            Create
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default CreateExternalCompanyModal;
